import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { checkSubscriptionStatus } from '@/lib/checkSubscription';
import { fetchEnterpriseAccess } from '@/hooks/useEnterpriseAccess';
import { ENTERPRISE_PLAN_ID } from '@/hooks/useOrgPlan';
import { activePlanCohorts, type PlanCohort } from '@/lib/myPlanAccess';

interface UserAccess {
  hasPaidAccess: boolean;
  hasActiveCohort: boolean;
  hasEnterpriseAccess: boolean;
  hasProAccess: boolean;
  activeCohorts: PlanCohort[];
  accessError: boolean;
  isLoading: boolean;
}

export function useUserAccess(): UserAccess {
  const { user, profile, session } = useAuth();
  const [hasPaidAccess, setHasPaidAccess] = useState(false);
  const [hasActiveCohort, setHasActiveCohort] = useState(false);
  const [hasEnterpriseAccess, setHasEnterpriseAccess] = useState(false);
  const [hasProAccess, setHasProAccess] = useState(false);
  const [activeCohorts, setActiveCohorts] = useState<PlanCohort[]>([]);
  const [accessError, setAccessError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setHasPaidAccess(false);
      setHasActiveCohort(false);
      setHasEnterpriseAccess(false);
      setHasProAccess(false);
      setActiveCohorts([]);
      setAccessError(false);
      setIsLoading(false);
      return;
    }

    const checkAccess = async () => {
      setIsLoading(true);
      setAccessError(false);
      try {
        // Check DB sources and Stripe in parallel
        const [cohortRes, subRes, stripeRes, entAccess] = await Promise.all([
          supabase
            .from('cohort_participants')
            .select('id, cohorts(id, name, content_access_deadline, is_perpetual, programs(name))')
            .eq('user_id', user.id),

          // Only manual/courtesy subs are trusted from the local table.
          // Stripe-backed access is validated server-side (price-filtered) by check-subscription.
          supabase
            .from('user_subscriptions')
            .select('id, status, provider, current_period_end, plan_id')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .or('provider.is.null,provider.eq.manual'),
          session?.access_token
            ? checkSubscriptionStatus().then((data) => ({ data })).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
          // Enterprise: licencia asignada + empresa Enterprise con contrato vigente (servidor).
          fetchEnterpriseAccess().catch(() => null),
        ]);

        // Self-paced (perpetual) route memberships are NOT guided cohorts:
        // they never grant paid access nor "active cohort" behaviour (calendar, cohort dashboard).
        const guidedRows = (cohortRes.data ?? []).filter((row: any) => row?.cohorts?.is_perpetual !== true);
        const hasCohort = guidedRows.length > 0;

        // Cohort-based access expires once the cohort's content access deadline passes.
        const now = Date.now();
        const validCohorts = activePlanCohorts((cohortRes.data ?? []).flatMap(row => row.cohorts ? [row.cohorts] : []), new Date(now));
        const hasValidCohortAccess = validCohorts.length > 0;
        setActiveCohorts(validCohorts);
        setAccessError(!!cohortRes.error || !!subRes.error || stripeRes.data === null || entAccess === null);

        // Manual/courtesy Pro license counts as paid access — pero solo si la
        // fecha de fin (si tiene) no pasó ya. No depende de que el cron
        // expire-licenses ya haya corrido: se revisa en vivo en cada check.
        const hasManualSub = (subRes.data ?? []).some((s: any) => {
          if (s.plan_id === ENTERPRISE_PLAN_ID) return false; // se evalúa aparte con el contrato
          if (!s.current_period_end) return true;
          return new Date(s.current_period_end).getTime() >= now;
        });

        // Check Stripe directly (already filtered by configured price in check-subscription)
        let stripeSubscribed = false;
        if (stripeRes.data && typeof stripeRes.data === 'object' && 'subscribed' in stripeRes.data) {
          stripeSubscribed = stripeRes.data.subscribed === true;
        }

        setHasActiveCohort(hasCohort);
        setHasEnterpriseAccess(!!entAccess?.grantsAccess);
        setHasProAccess(hasManualSub || stripeSubscribed);
        // NOTE: Belonging to an organization no longer grants paid access automatically.
        // Enterprise users must have an active subscription assigned by their company admin
        // (recorded in user_subscriptions via toggle_enterprise_license).
        setHasPaidAccess(hasValidCohortAccess || hasManualSub || stripeSubscribed || !!entAccess?.grantsAccess);

      } catch (error) {
        setAccessError(true);
        console.error('Error checking user access:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAccess();
  }, [user, session?.access_token, profile?.organization_id]);

  return { hasPaidAccess, hasActiveCohort, hasEnterpriseAccess, hasProAccess, activeCohorts, accessError, isLoading };
}
